import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import AppLayout from '../../components/layout/AppLayout';
import Modal from '../../components/ui/Modal';
import FormInput from '../../components/forms/FormInput';
import { getStaff, getTodayRoster, getRosterByDate, filterStaff, createStaff, updateStaff, deleteStaff, getAttendance, saveAttendance, uploadStaffPhoto, uploadStaffAadhar } from '../../api/staff';
import { getSchedule, getDailyLogs, getDailySummary, toggleTaskLog, getCleaningStaff, getAssignments, assignStaff, unassignStaff, autoAssignStaff } from '../../api/housekeeping';
import { getSecPosts, getSecurityStaff, getSecAssignments, assignSecStaff, unassignSecStaff, autoAssignSecurity } from '../../api/security';


const tabs = [
  { key: 'all',      label: 'Housekeeping Schedule' },
  { key: 'security', label: 'Security Schedule' },
  { key: 'staff',    label: 'Staff Directory' },
  { key: 'attendance', label: 'Attendance Tracker' },
  { key: 'leave', label: 'Leave Management' },
  { key: 'reports', label: 'Reports' },
];

const towerAFloors = ['1','2','3','4','5','6','7','8','9','10','11','12','13','14','15','16','17','18'];
const towerBFloors = Array.from({ length: 18 }, (_, i) => String(i + 1));
const securityPostsList = ['Gate 1', 'Gate 2', 'Main Entrance', 'Common Area'];
const towerCleaningSections = ['Floor Mopping', 'Floor Sweeping', 'Garbage Removal'];
const parkingCleaningSections = ['Parking B1', 'Parking B2', 'Parking 1'];
const otherCleaningSections = ['Lobby GF', 'Watering Plants', 'Conference Hall', 'Ladies Gym', 'Multi Gym', 'Common Toilets', 'Front Area', 'Back Side', 'Tracks', 'Games Room'];
const allCleaningSections = [...towerCleaningSections, ...parkingCleaningSections, ...otherCleaningSections];

const allDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const dayLabels = { Monday: 'Mon', Tuesday: 'Tue', Wednesday: 'Wed', Thursday: 'Thu', Friday: 'Fri', Saturday: 'Sat', Sunday: 'Sun' };

const shiftOptions = [
  { value: '', label: 'All Shifts' },
  { value: 'day', label: 'Day Shift' },
  { value: 'evening', label: 'Evening Shift' },
  { value: 'night', label: 'Night Shift' },
];

const formatTime12 = (time24) => {
  if (!time24) return '';
  const [h, m] = time24.split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour % 12 || 12;
  return `${h12}:${m} ${ampm}`;
};

const getInitials = (name) => {
  if (!name) return '??';
  return name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2);
};

const getAssignmentSummary = (member) => {
  if (!member.assignments || member.assignments.length === 0) return null;
  if (member.staff_type === 'security') {
    return { posts: member.assignments.map((a) => a.security_post).filter(Boolean) };
  }
  const towers = {};
  const sections = [];
  member.assignments.forEach((a) => {
    if (a.assignment_type === 'tower_floor' && a.tower) {
      if (!towers[a.tower]) towers[a.tower] = [];
      if (a.floor) towers[a.tower].push(a.floor);
    }
    if (a.assignment_type === 'cleaning_section' && a.cleaning_section) {
      sections.push(a.cleaning_section);
    }
  });
  return { towers, sections };
};

// ── Security Schedule components ─────────────────────────────────────────────

const SEC_SHIFTS = [
  { key: 'day',     label: 'Day Shift',     time: '6:00 AM – 2:00 PM',  color: '#043959', light: 'rgba(4,57,89,0.07)'     },
  { key: 'evening', label: 'Evening Shift', time: '2:00 PM – 10:00 PM', color: '#8B5CF6', light: 'rgba(139,92,246,0.07)'  },
  { key: 'night',   label: 'Night Shift',   time: '10:00 PM – 6:00 AM', color: '#1E293B', light: 'rgba(30,41,59,0.07)'    },
];

const SEC_AVATAR_COLORS = [
  '#3B82F6','#8B5CF6','#EC4899','#F97316','#14B8A6',
  '#EF4444','#06B6D4','#84CC16','#F59E0B','#6366F1',
];

function secAvatarColor(id) { return SEC_AVATAR_COLORS[id % SEC_AVATAR_COLORS.length]; }
function secInitials(name)  { return name ? name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2) : '?'; }
function secTodayStr()      { return new Date().toISOString().slice(0, 10); }

function SecStaffPicker({ postId, shift, date, allStaff, assigned, onAssign, onUnassign, shiftMap, posts }) {
  const [open,     setOpen]     = useState(false);
  const [busy,     setBusy]     = useState(null);
  const [conflict, setConflict] = useState(null); // { staff, oldPostName, oldAssignmentId, oldPostId }
  const ref = useRef();

  useEffect(() => {
    function handle(e) { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setConflict(null); } }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const assignedIds = new Set(assigned.map(a => a.staff_id));
  const available   = allStaff.filter(s => !assignedIds.has(s.id));

  // Find if a staff member is already assigned to a different post in this shift
  const findConflict = (staffId) => {
    for (const [pid, list] of Object.entries(shiftMap || {})) {
      if (Number(pid) === postId) continue;
      const hit = (list || []).find(a => a.staff_id === staffId);
      if (hit) {
        const postName = (posts || []).find(p => p.id === Number(pid))?.name || `Post #${pid}`;
        return { assignmentId: hit.assignment_id, postId: Number(pid), postName };
      }
    }
    return null;
  };

  const handleAssign = async (staffId, staffName) => {
    const clash = findConflict(staffId);
    if (clash) {
      setConflict({ staffId, staffName, ...clash });
      return;
    }
    setBusy(staffId);
    try { await onAssign(postId, shift, staffId, date); } finally { setBusy(null); }
  };

  const handleMoveConfirm = async () => {
    if (!conflict) return;
    setBusy(conflict.staffId);
    try {
      await onUnassign(conflict.assignmentId, shift, conflict.postId);
      await onAssign(postId, shift, conflict.staffId, date);
      setConflict(null);
      setOpen(false);
    } finally { setBusy(null); }
  };

  const handleUnassign = async (assignmentId) => {
    setBusy(assignmentId);
    try { await onUnassign(assignmentId, shift, postId); } finally { setBusy(null); }
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => { setOpen(o => !o); setConflict(null); }}
        title="Assign staff"
        style={{
          width: 26, height: 26, borderRadius: 6, border: '1.5px dashed #D0D0CC',
          background: open ? '#F0F0EB' : 'white', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          color: '#999', fontSize: 16, fontWeight: 300,
        }}
      >+</button>

      {open && (
        <div style={{
          position: 'absolute', zIndex: 100, top: 30, right: 0,
          background: 'white', border: '1px solid #E5E5E0', borderRadius: 12,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: 240,
          padding: '12px 0', overflow: 'hidden',
        }}>

          {/* ── Conflict confirmation view ── */}
          {conflict ? (
            <div style={{ padding: '4px 14px 12px' }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12,
                padding: '10px 12px', borderRadius: 10, background: 'rgba(245,158,11,0.08)',
                border: '1px solid rgba(245,158,11,0.25)',
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                <div>
                  <div className="text-sm font-semibold" style={{ color: '#1A1A1A' }}>Already Assigned</div>
                  <div className="text-xs mt-0.5" style={{ color: '#666' }}>
                    <strong>{conflict.staffName}</strong> is on <strong>{conflict.postName}</strong> this shift.
                  </div>
                </div>
              </div>
              <p className="text-xs mb-3" style={{ color: '#888', lineHeight: 1.5 }}>
                Move <strong>{conflict.staffName}</strong> from <strong>{conflict.postName}</strong> to this post?
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setConflict(null)}
                  style={{
                    flex: 1, padding: '7px 0', borderRadius: 8, border: '1px solid #E5E5E0',
                    background: 'white', color: '#666', fontSize: 12, fontWeight: 500,
                    cursor: 'pointer', fontFamily: "'Lexend', sans-serif",
                  }}
                >Cancel</button>
                <button
                  onClick={handleMoveConfirm}
                  disabled={!!busy}
                  style={{
                    flex: 1, padding: '7px 0', borderRadius: 8, border: 'none',
                    background: '#043959', color: 'white', fontSize: 12, fontWeight: 600,
                    cursor: busy ? 'wait' : 'pointer', fontFamily: "'Lexend', sans-serif",
                    opacity: busy ? 0.7 : 1,
                  }}
                >{busy ? 'Moving…' : 'Move Here'}</button>
              </div>
            </div>
          ) : (
            <>
              {assigned.length > 0 && (
                <div style={{ padding: '0 12px 8px', borderBottom: '1px solid #F0F0EB', marginBottom: 8 }}>
                  <div className="text-xs font-semibold mb-2" style={{ color: '#999', letterSpacing: '0.5px' }}>ON DUTY</div>
                  {assigned.map(a => (
                    <div key={a.assignment_id} className="flex items-center gap-2 py-1.5">
                      <div style={{
                        width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                        background: secAvatarColor(a.staff_id), color: 'white',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 10, fontWeight: 700,
                      }}>{secInitials(a.staff_name)}</div>
                      <span className="flex-1 text-sm" style={{ color: '#1A1A1A' }}>{a.staff_name}</span>
                      <button
                        onClick={() => handleUnassign(a.assignment_id)}
                        disabled={busy === a.assignment_id}
                        style={{ color: '#EF4444', background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px', fontSize: 16, lineHeight: 1 }}
                      >×</button>
                    </div>
                  ))}
                </div>
              )}
              {available.length > 0 ? (
                <div style={{ padding: '0 12px' }}>
                  <div className="text-xs font-semibold mb-2" style={{ color: '#999', letterSpacing: '0.5px' }}>ASSIGN GUARD</div>
                  <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                    {available.map(s => {
                      const hasConflict = !!findConflict(s.id);
                      return (
                        <button
                          key={s.id}
                          onClick={() => handleAssign(s.id, s.name)}
                          disabled={busy === s.id}
                          className="w-full flex items-center gap-2 rounded-lg text-left"
                          style={{ background: 'none', border: 'none', cursor: busy === s.id ? 'wait' : 'pointer', padding: '6px 4px' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#F5F5F5'}
                          onMouseLeave={e => e.currentTarget.style.background = 'none'}
                        >
                          <div style={{
                            width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                            background: secAvatarColor(s.id), color: 'white',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 10, fontWeight: 700,
                          }}>{secInitials(s.name)}</div>
                          <span className="flex-1 text-sm" style={{ color: '#1A1A1A' }}>{s.name}</span>
                          {hasConflict && (
                            <span style={{ fontSize: 10, color: '#F59E0B', fontWeight: 600, whiteSpace: 'nowrap' }}>on duty</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="text-xs text-center" style={{ color: '#999', padding: '4px 12px' }}>
                  {allStaff.length === 0 ? 'No security staff found' : 'All staff assigned'}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function SecurityScheduleTab() {
  const [posts,         setPosts]         = useState([]);
  const [allStaff,      setAllStaff]      = useState([]);
  const [assignments,   setAssignments]   = useState({ day: {}, evening: {}, night: {} });
  const [date,          setDate]          = useState(secTodayStr());
  const [loading,       setLoading]       = useState(true);
  const [autoAssigning, setAutoAssigning] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [postsRes, staffRes, asgRes] = await Promise.all([
        getSecPosts(), getSecurityStaff(), getSecAssignments(date),
      ]);
      setPosts(postsRes.data || []);
      setAllStaff(staffRes.data || []);

      const raw = asgRes.data || { day: {}, evening: {}, night: {} };
      // Ensure integer keys
      const normalised = {};
      for (const shift of ['day', 'evening', 'night']) {
        normalised[shift] = {};
        Object.entries(raw[shift] || {}).forEach(([pid, list]) => {
          normalised[shift][Number(pid)] = list;
        });
      }
      setAssignments(normalised);

      // Auto-assign if no assignments exist
      const hasAny = Object.values(raw).some(s => Object.keys(s).length > 0);
      if (!hasAny && (staffRes.data || []).length > 0) {
        try {
          const autoRes = await autoAssignSecurity(date);
          const autoRaw = autoRes.data || {};
          const autoNorm = {};
          for (const shift of ['day', 'evening', 'night']) {
            autoNorm[shift] = {};
            Object.entries(autoRaw[shift] || {}).forEach(([pid, list]) => {
              autoNorm[shift][Number(pid)] = list;
            });
          }
          setAssignments(autoNorm);
        } catch (_) {}
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [date]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleAssign = async (postId, shift, staffId) => {
    const res = await assignSecStaff(postId, shift, staffId, date);
    setAssignments(prev => ({
      ...prev,
      [shift]: { ...prev[shift], [postId]: [...(prev[shift][postId] || []), res.data] },
    }));
  };

  const handleUnassign = async (assignmentId, shift, postId) => {
    await unassignSecStaff(assignmentId);
    setAssignments(prev => ({
      ...prev,
      [shift]: {
        ...prev[shift],
        [postId]: (prev[shift][postId] || []).filter(a => a.assignment_id !== assignmentId),
      },
    }));
  };

  const handleAutoAssign = async () => {
    if (!window.confirm(`Auto-assign ${allStaff.length} security staff across all shifts and posts for ${date}? This will replace existing assignments.`)) return;
    setAutoAssigning(true);
    try {
      const res = await autoAssignSecurity(date);
      const raw = res.data || {};
      const normalised = {};
      for (const shift of ['day', 'evening', 'night']) {
        normalised[shift] = {};
        Object.entries(raw[shift] || {}).forEach(([pid, list]) => {
          normalised[shift][Number(pid)] = list;
        });
      }
      setAssignments(normalised);
    } catch (err) { console.error(err); alert('Auto-assign failed. Please try again.'); }
    finally { setAutoAssigning(false); }
  };

  const totalCovered = Object.values(assignments).reduce(
    (sum, shiftMap) => sum + Object.values(shiftMap).filter(list => list.length > 0).length, 0
  );
  const totalSlots = SEC_SHIFTS.length * posts.length;

  return (
    <div style={{ maxWidth: 960, margin: '0 auto' }}>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="text-lg font-bold" style={{ color: '#1A1A1A' }}>Security Shift Schedule</h3>
          <p className="text-sm mt-0.5" style={{ color: '#999' }}>Assign security guards to posts across day, evening and night shifts</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleAutoAssign}
            disabled={autoAssigning || allStaff.length === 0}
            className="text-sm font-medium rounded-lg text-white transition-all hover:opacity-90 flex items-center gap-2"
            style={{ padding: '8px 16px', background: '#043959', opacity: autoAssigning ? 0.7 : 1, cursor: autoAssigning ? 'wait' : 'pointer', border: 'none', fontFamily: "'Lexend', sans-serif" }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            {autoAssigning ? 'Assigning...' : `Auto-Assign (${allStaff.length} staff)`}
          </button>
          <input
            type="date" value={date}
            onChange={e => setDate(e.target.value)}
            className="text-sm rounded-lg"
            style={{ padding: '8px 12px', border: '1px solid #E5E5E0', fontFamily: "'Lexend', sans-serif", outline: 'none' }}
          />
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
        {[
          { label: 'Security Posts',  value: posts.length,   color: '#043959' },
          { label: 'Total Slots',     value: totalSlots,     color: '#8B5CF6' },
          { label: 'Posts Covered',   value: totalCovered,   color: '#10B981' },
          { label: 'Security Staff',  value: allStaff.length, color: '#F59E0B' },
        ].map(c => (
          <div key={c.label} className="bg-white rounded-2xl text-center" style={{ border: '1px solid #E5E5E0', padding: '16px 12px' }}>
            <div className="text-2xl font-bold mb-0.5" style={{ color: c.color }}>{c.value}</div>
            <div className="text-xs" style={{ color: '#999' }}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* Progress bar */}
      {totalSlots > 0 && (
        <div className="bg-white rounded-2xl mb-5" style={{ border: '1px solid #E5E5E0', padding: '14px 20px' }}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium" style={{ color: '#1A1A1A' }}>Post Coverage</span>
            <span className="text-sm font-semibold" style={{ color: '#10B981' }}>{totalCovered}/{totalSlots} covered · {Math.round((totalCovered / totalSlots) * 100)}%</span>
          </div>
          <div style={{ height: 7, background: '#F0F0EB', borderRadius: 99 }}>
            <div style={{
              height: '100%', borderRadius: 99,
              background: totalCovered === totalSlots ? '#10B981' : '#3B82F6',
              width: `${totalSlots > 0 ? Math.round((totalCovered / totalSlots) * 100) : 0}%`,
              transition: 'width 0.4s ease',
            }} />
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-16 text-sm" style={{ color: '#999' }}>Loading schedule...</div>
      ) : (
        <div className="flex flex-col gap-4">
          {SEC_SHIFTS.map((shift) => {
            const shiftMap    = assignments[shift.key] || {};
            const coveredCount = posts.filter(p => (shiftMap[p.id] || []).length > 0).length;

            return (
              <div key={shift.key} className="bg-white rounded-2xl"
                style={{ border: '1px solid #E5E5E0', borderLeft: `4px solid ${shift.color}` }}>

                {/* Shift header */}
                <div className="flex flex-wrap items-center justify-between gap-2"
                  style={{ padding: '13px 20px', background: shift.light, borderBottom: posts.length > 0 ? '1px solid #E5E5E0' : 'none', borderRadius: '16px 16px 0 0' }}>
                  <div className="flex items-center gap-3">
                    <div className="text-xs font-semibold whitespace-nowrap" style={{ color: shift.color, minWidth: 120 }}>
                      {shift.time}
                    </div>
                    <div>
                      <div className="font-semibold text-sm" style={{ color: '#1A1A1A' }}>{shift.label}</div>
                      <div className="text-xs mt-0.5" style={{ color: '#999' }}>
                        {posts.length} post{posts.length !== 1 ? 's' : ''} · {posts.reduce((s, p) => s + p.staff_count, 0)} guard{posts.reduce((s, p) => s + p.staff_count, 0) !== 1 ? 's' : ''} required
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold px-2 py-1 rounded-lg"
                      style={{
                        background: coveredCount === posts.length ? 'rgba(16,185,129,0.15)' : 'rgba(0,0,0,0.06)',
                        color: coveredCount === posts.length ? '#10B981' : '#666',
                      }}>
                      {coveredCount}/{posts.length} covered
                    </span>
                    <span className="text-xs font-semibold px-2 py-1 rounded-full"
                      style={{ background: shift.color, color: 'white' }}>
                      {shift.label.split(' ')[0]}
                    </span>
                  </div>
                </div>

                {/* Post rows */}
                {posts.length > 0 && (
                  <div style={{ padding: '10px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {posts.map((post) => {
                      const assigned = shiftMap[post.id] || [];
                      const needed   = post.staff_count - assigned.length;

                      return (
                        <div key={post.id} className="rounded-xl"
                          style={{
                            padding: '10px 14px',
                            background: assigned.length > 0 ? 'rgba(16,185,129,0.04)' : '#FAFAF8',
                            border: `1px solid ${assigned.length > 0 ? 'rgba(16,185,129,0.2)' : '#F0F0EB'}`,
                            display: 'flex', alignItems: 'center', gap: 12,
                          }}>

                          {/* Post status dot */}
                          <div style={{
                            width: 9, height: 9, borderRadius: '50%', flexShrink: 0,
                            background: assigned.length >= post.staff_count ? '#10B981' : assigned.length > 0 ? '#F59E0B' : '#D0D0CC',
                          }} />

                          {/* Post name */}
                          <span className="text-sm font-medium" style={{ color: '#1A1A1A', minWidth: 110 }}>
                            {post.name}
                          </span>

                          {/* Right: badges + assigned names + picker */}
                          <div className="flex items-center gap-2 flex-wrap flex-1" style={{ justifyContent: 'flex-end' }}>
                            <div className="flex items-center gap-1 text-xs font-semibold"
                              style={{ padding: '3px 9px', borderRadius: 12, background: 'rgba(4,57,89,0.08)', color: '#043959', whiteSpace: 'nowrap' }}>
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                              </svg>
                              {post.staff_count} req
                            </div>

                            {assigned.map(a => (
                              <div key={a.assignment_id} className="flex items-center gap-1" style={{ fontSize: 12 }}>
                                <div style={{ width: 7, height: 7, borderRadius: '50%', background: secAvatarColor(a.staff_id), flexShrink: 0 }}/>
                                <span style={{ color: '#444', fontWeight: 500, whiteSpace: 'nowrap' }}>{a.staff_name}</span>
                                <button
                                  onClick={() => handleUnassign(a.assignment_id, shift.key, post.id)}
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#CCC', padding: 0, lineHeight: 1, fontSize: 13 }}
                                  title="Remove"
                                >×</button>
                              </div>
                            ))}

                            {needed > 0 && <span style={{ fontSize: 11, color: '#F59E0B', fontWeight: 500, whiteSpace: 'nowrap' }}>{needed} more</span>}
                            {needed <= 0 && assigned.length > 0 && <span style={{ fontSize: 11, color: '#10B981', fontWeight: 500 }}>✓ Full</span>}

                            <SecStaffPicker
                              postId={post.id} shift={shift.key} date={date}
                              allStaff={allStaff} assigned={assigned}
                              onAssign={handleAssign} onUnassign={handleUnassign}
                              shiftMap={shiftMap} posts={posts}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="text-center text-xs mt-8 pb-4" style={{ color: '#CCC' }}>
        {SEC_SHIFTS.length} shifts · {posts.length} posts · {allStaff.length} security staff
      </div>
    </div>
  );
}

// ── Housekeeping components ───────────────────────────────────────────────────

const HK_TYPE = {
  reporting: { bg: '#043959', light: 'rgba(4,57,89,0.07)',    label: 'Reporting' },
  work:      { bg: '#10B981', light: 'rgba(16,185,129,0.07)', label: 'Work'      },
  break:     { bg: '#F59E0B', light: 'rgba(245,158,11,0.07)', label: 'Break'     },
};

const HK_AVATAR_COLORS = [
  '#3B82F6','#8B5CF6','#EC4899','#F97316','#14B8A6',
  '#EF4444','#06B6D4','#84CC16','#F59E0B','#6366F1',
];

function hkAvatarColor(id) { return HK_AVATAR_COLORS[id % HK_AVATAR_COLORS.length]; }
function hkInitials(name) { return name ? name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2) : '?'; }
function hkFormatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hour = parseInt(h, 10);
  return `${hour % 12 || 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
}
function hkTodayStr() { return new Date().toISOString().slice(0, 10); }

function HKStaffPicker({ task, date, allStaff, assigned, onAssign, onUnassign, blockAssignments, blockTasks }) {
  const [open,     setOpen]     = useState(false);
  const [busy,     setBusy]     = useState(null);
  const [conflict, setConflict] = useState(null); // { staffId, staffName, oldTaskId, oldTaskArea, oldAssignmentId }
  const ref = useRef();

  useEffect(() => {
    function handle(e) { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setConflict(null); } }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const assignedIds = new Set(assigned.map(a => a.staff_id));
  const available   = allStaff.filter(s => !assignedIds.has(s.id));
  const needed      = task.staff_count - assigned.length;

  // Find if staff is already on another task in the same time block
  const findConflict = (staffId) => {
    for (const t of (blockTasks || [])) {
      if (t.id === task.id) continue;
      const hit = (blockAssignments?.[t.id] || []).find(a => a.staff_id === staffId);
      if (hit) return { assignmentId: hit.assignment_id, taskId: t.id, taskArea: t.area };
    }
    return null;
  };

  const handleAssign = async (staffId, staffName) => {
    const clash = findConflict(staffId);
    if (clash) {
      setConflict({ staffId, staffName, ...clash });
      return;
    }
    setBusy(staffId);
    try { await onAssign(task.id, staffId, date); } finally { setBusy(null); }
  };

  const handleMoveConfirm = async () => {
    if (!conflict) return;
    setBusy(conflict.staffId);
    try {
      await onUnassign(conflict.assignmentId, conflict.taskId);
      await onAssign(task.id, conflict.staffId, date);
      setConflict(null);
      setOpen(false);
    } finally { setBusy(null); }
  };

  const handleUnassign = async (assignmentId) => {
    setBusy(assignmentId);
    try { await onUnassign(assignmentId, task.id); } finally { setBusy(null); }
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => { setOpen(o => !o); setConflict(null); }}
        title="Assign staff"
        style={{
          width: 26, height: 26, borderRadius: 6, border: '1.5px dashed #D0D0CC',
          background: open ? '#F0F0EB' : 'white', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          color: '#999', fontSize: 16, fontWeight: 300,
        }}
      >+</button>

      {open && (
        <div style={{
          position: 'absolute', zIndex: 100, top: 30, right: 0,
          background: 'white', border: '1px solid #E5E5E0', borderRadius: 12,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: 240,
          padding: '12px 0', overflow: 'hidden',
        }}>

          {/* ── Conflict confirmation view ── */}
          {conflict ? (
            <div style={{ padding: '4px 14px 12px' }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12,
                padding: '10px 12px', borderRadius: 10, background: 'rgba(245,158,11,0.08)',
                border: '1px solid rgba(245,158,11,0.25)',
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                <div>
                  <div className="text-sm font-semibold" style={{ color: '#1A1A1A' }}>Already Assigned</div>
                  <div className="text-xs mt-0.5" style={{ color: '#666' }}>
                    <strong>{conflict.staffName}</strong> is on <strong>{conflict.taskArea}</strong> this block.
                  </div>
                </div>
              </div>
              <p className="text-xs mb-3" style={{ color: '#888', lineHeight: 1.5 }}>
                Move <strong>{conflict.staffName}</strong> from <strong>{conflict.taskArea}</strong> to <strong>{task.area}</strong>?
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setConflict(null)}
                  style={{
                    flex: 1, padding: '7px 0', borderRadius: 8, border: '1px solid #E5E5E0',
                    background: 'white', color: '#666', fontSize: 12, fontWeight: 500,
                    cursor: 'pointer', fontFamily: "'Lexend', sans-serif",
                  }}
                >Cancel</button>
                <button
                  onClick={handleMoveConfirm}
                  disabled={!!busy}
                  style={{
                    flex: 1, padding: '7px 0', borderRadius: 8, border: 'none',
                    background: '#043959', color: 'white', fontSize: 12, fontWeight: 600,
                    cursor: busy ? 'wait' : 'pointer', fontFamily: "'Lexend', sans-serif",
                    opacity: busy ? 0.7 : 1,
                  }}
                >{busy ? 'Moving…' : 'Move Here'}</button>
              </div>
            </div>
          ) : (
            <>
              {assigned.length > 0 && (
                <div style={{ padding: '0 12px 8px', borderBottom: '1px solid #F0F0EB', marginBottom: 8 }}>
                  <div className="text-xs font-semibold mb-2" style={{ color: '#999', letterSpacing: '0.5px' }}>ASSIGNED</div>
                  {assigned.map(a => (
                    <div key={a.assignment_id} className="flex items-center gap-2 py-1.5">
                      <div style={{
                        width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                        background: hkAvatarColor(a.staff_id), color: 'white',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 10, fontWeight: 700,
                      }}>{hkInitials(a.staff_name)}</div>
                      <span className="flex-1 text-sm" style={{ color: '#1A1A1A' }}>{a.staff_name}</span>
                      <button
                        onClick={() => handleUnassign(a.assignment_id)}
                        disabled={busy === a.assignment_id}
                        style={{ color: '#EF4444', background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px', fontSize: 16, lineHeight: 1 }}
                      >×</button>
                    </div>
                  ))}
                </div>
              )}
              {available.length > 0 ? (
                <div style={{ padding: '0 12px' }}>
                  <div className="text-xs font-semibold mb-2" style={{ color: '#999', letterSpacing: '0.5px' }}>
                    ADD STAFF {needed > 0 && <span style={{ color: '#F59E0B' }}>({needed} needed)</span>}
                  </div>
                  <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                    {available.map(s => {
                      const hasConflict = !!findConflict(s.id);
                      return (
                        <button
                          key={s.id}
                          onClick={() => handleAssign(s.id, s.name)}
                          disabled={busy === s.id}
                          className="w-full flex items-center gap-2 rounded-lg text-left"
                          style={{ background: 'none', border: 'none', cursor: busy === s.id ? 'wait' : 'pointer', padding: '6px 4px' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#F5F5F5'}
                          onMouseLeave={e => e.currentTarget.style.background = 'none'}
                        >
                          <div style={{
                            width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                            background: hkAvatarColor(s.id), color: 'white',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 10, fontWeight: 700,
                          }}>{hkInitials(s.name)}</div>
                          <span className="flex-1 text-sm" style={{ color: '#1A1A1A' }}>{s.name}</span>
                          {hasConflict && (
                            <span style={{ fontSize: 10, color: '#F59E0B', fontWeight: 600, whiteSpace: 'nowrap' }}>busy</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="text-xs text-center py-2" style={{ color: '#999', padding: '4px 12px' }}>
                  {allStaff.length === 0 ? 'No cleaning staff found' : 'All staff assigned'}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function HousekeepingTab() {
  const [schedule,      setSchedule]      = useState([]);
  const [logs,          setLogs]          = useState({});
  const [summary,       setSummary]       = useState(null);
  const [assignments,   setAssignments]   = useState({});
  const [allStaff,      setAllStaff]      = useState([]);
  const [date,          setDate]          = useState(hkTodayStr());
  const [loading,       setLoading]       = useState(true);
  const [toggling,      setToggling]      = useState(null);
  const [autoAssigning, setAutoAssigning] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [schRes, logRes, sumRes, asgRes, staffRes] = await Promise.all([
        getSchedule(), getDailyLogs(date), getDailySummary(date), getAssignments(date), getCleaningStaff(),
      ]);
      setSchedule(schRes.data || []);
      const logsObj = {};
      Object.values(logRes.data || {}).forEach(l => { logsObj[l.task_id] = l; });
      setLogs(logsObj);
      setSummary(sumRes.data || null);
      const rawAsg = asgRes.data || {};
      const asgObj = {};
      Object.entries(rawAsg).forEach(([tid, list]) => { asgObj[Number(tid)] = list; });
      setAssignments(asgObj);
      const staff = staffRes.data || [];
      setAllStaff(staff);
      const hasAny = Object.keys(rawAsg).length > 0;
      if (!hasAny && staff.length > 0) {
        try {
          const autoRes = await autoAssignStaff(date);
          const rawAuto = autoRes.data || {};
          const autoObj = {};
          Object.entries(rawAuto).forEach(([tid, list]) => { autoObj[Number(tid)] = list; });
          setAssignments(autoObj);
        } catch (_) {}
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [date]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleToggle = async (taskId) => {
    setToggling(taskId);
    try {
      const res = await toggleTaskLog(taskId, date);
      const updated = res.data;
      setLogs(prev => ({ ...prev, [taskId]: updated }));
      setSummary(prev => {
        if (!prev) return prev;
        const nowDone = updated.status === 'completed';
        const completed = nowDone ? prev.completed + 1 : prev.completed - 1;
        return { ...prev, completed, pending: prev.total - completed, percentage: prev.total > 0 ? Math.round((completed / prev.total) * 100) : 0 };
      });
    } finally { setToggling(null); }
  };

  const handleAssign = async (taskId, staffId) => {
    const res = await assignStaff(taskId, staffId, date);
    setAssignments(prev => ({ ...prev, [taskId]: [...(prev[taskId] || []), res.data] }));
  };

  const handleUnassign = async (assignmentId, taskId) => {
    await unassignStaff(assignmentId);
    setAssignments(prev => ({ ...prev, [taskId]: (prev[taskId] || []).filter(a => a.assignment_id !== assignmentId) }));
  };

  const handleAutoAssign = async () => {
    if (!window.confirm(`Auto-assign all ${allStaff.length} cleaning staff across tasks for ${date}? This will replace existing assignments.`)) return;
    setAutoAssigning(true);
    try {
      const res = await autoAssignStaff(date);
      const rawAsg = res.data || {};
      const asgObj = {};
      Object.entries(rawAsg).forEach(([tid, list]) => { asgObj[Number(tid)] = list; });
      setAssignments(asgObj);
    } catch (err) { console.error(err); alert('Auto-assign failed. Please try again.'); }
    finally { setAutoAssigning(false); }
  };

  const totalStaff = schedule
    .filter(b => b.block_type === 'work')
    .reduce((s, b) => s + b.tasks.reduce((a, t) => a + t.staff_count, 0), 0);

  return (
    <div style={{ maxWidth: 960, margin: '0 auto' }}>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="text-lg font-bold" style={{ color: '#1A1A1A' }}>Daily Work Schedule</h3>
          <p className="text-sm mt-0.5" style={{ color: '#999' }}>Assign cleaning staff to tasks and track daily completion</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleAutoAssign}
            disabled={autoAssigning || allStaff.length === 0}
            className="text-sm font-medium rounded-lg text-white transition-all hover:opacity-90 flex items-center gap-2"
            style={{ padding: '8px 16px', background: '#043959', opacity: autoAssigning ? 0.7 : 1, cursor: autoAssigning ? 'wait' : 'pointer', border: 'none', fontFamily: "'Lexend', sans-serif" }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            {autoAssigning ? 'Assigning...' : `Auto-Assign (${allStaff.length} staff)`}
          </button>
          <input
            type="date" value={date}
            onChange={e => setDate(e.target.value)}
            className="text-sm rounded-lg"
            style={{ padding: '8px 12px', border: '1px solid #E5E5E0', fontFamily: "'Lexend', sans-serif", outline: 'none' }}
          />
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
          {[
            { label: 'Total Tasks',    value: summary.total,    color: '#043959' },
            { label: 'Completed',      value: summary.completed, color: '#10B981' },
            { label: 'Pending',        value: summary.pending,   color: '#F59E0B' },
            { label: 'Cleaning Staff', value: allStaff.length,  color: '#8B5CF6' },
          ].map(c => (
            <div key={c.label} className="bg-white rounded-2xl text-center" style={{ border: '1px solid #E5E5E0', padding: '16px 12px' }}>
              <div className="text-2xl font-bold mb-0.5" style={{ color: c.color }}>{c.value}</div>
              <div className="text-xs" style={{ color: '#999' }}>{c.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Progress bar */}
      {summary && (
        <div className="bg-white rounded-2xl mb-5" style={{ border: '1px solid #E5E5E0', padding: '14px 20px' }}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium" style={{ color: '#1A1A1A' }}>Daily Completion</span>
            <span className="text-sm font-semibold" style={{ color: '#10B981' }}>{summary.completed}/{summary.total} done · {summary.percentage}%</span>
          </div>
          <div style={{ height: 7, background: '#F0F0EB', borderRadius: 99 }}>
            <div style={{
              height: '100%', borderRadius: 99,
              background: summary.percentage === 100 ? '#10B981' : '#3B82F6',
              width: `${summary.percentage}%`, transition: 'width 0.4s ease',
            }} />
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-16 text-sm" style={{ color: '#999' }}>Loading schedule...</div>
      ) : (
        <div className="flex flex-col gap-4">
          {schedule.map((block) => {
            const cfg    = HK_TYPE[block.block_type] || HK_TYPE.work;
            const isRest = block.block_type !== 'work';
            const blockDone = block.tasks.filter(t => logs[t.id]?.status === 'completed').length;
            // Map of taskId → assignments for all tasks in this block (for conflict detection)
            const blockAssignments = Object.fromEntries(block.tasks.map(t => [t.id, assignments[t.id] || []]));

            return (
              <div key={block.id} className="bg-white rounded-2xl"
                style={{ border: '1px solid #E5E5E0', borderLeft: `4px solid ${cfg.bg}` }}>

                {/* Block header */}
                <div className="flex flex-wrap items-center justify-between gap-2"
                  style={{ padding: '13px 20px', background: cfg.light, borderBottom: block.tasks.length > 0 ? '1px solid #E5E5E0' : 'none', borderRadius: '16px 16px 0 0' }}>
                  <div className="flex items-center gap-3">
                    <div className="text-xs font-semibold whitespace-nowrap" style={{ color: cfg.bg, minWidth: 100 }}>
                      {hkFormatTime(block.start_time)}{block.end_time && ` – ${hkFormatTime(block.end_time)}`}
                    </div>
                    <div>
                      <div className="font-semibold text-sm" style={{ color: '#1A1A1A' }}>{block.title}</div>
                      {!isRest && block.tasks.length > 0 && (
                        <div className="text-xs mt-0.5" style={{ color: '#999' }}>
                          {block.tasks.reduce((s, t) => s + t.staff_count, 0)} staff required · {block.tasks.length} area{block.tasks.length > 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!isRest && block.tasks.length > 0 && (
                      <span className="text-xs font-semibold px-2 py-1 rounded-lg"
                        style={{
                          background: blockDone === block.tasks.length ? 'rgba(16,185,129,0.15)' : 'rgba(0,0,0,0.06)',
                          color: blockDone === block.tasks.length ? '#10B981' : '#666',
                        }}>
                        {blockDone}/{block.tasks.length} done
                      </span>
                    )}
                    <span className="text-xs font-semibold px-2 py-1 rounded-full"
                      style={{ background: cfg.bg, color: 'white' }}>{cfg.label}</span>
                  </div>
                </div>

                {/* Task rows */}
                {block.tasks.length > 0 && (
                  <div style={{ padding: '10px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {block.tasks.map((task) => {
                      const log      = logs[task.id];
                      const done     = log?.status === 'completed';
                      const assigned = assignments[task.id] || [];
                      const needed   = task.staff_count - assigned.length;
                      const isBusy   = toggling === task.id;

                      return (
                        <div key={task.id} className="rounded-xl transition-all"
                          style={{
                            padding: '10px 14px',
                            background: done ? 'rgba(16,185,129,0.05)' : '#FAFAF8',
                            border: `1px solid ${done ? 'rgba(16,185,129,0.2)' : '#F0F0EB'}`,
                            display: 'flex', alignItems: 'flex-start', gap: 12,
                          }}>

                          <button
                            onClick={() => handleToggle(task.id)}
                            disabled={isBusy}
                            style={{
                              width: 22, height: 22, borderRadius: 5, flexShrink: 0, marginTop: 1,
                              border: done ? 'none' : '2px solid #D0D0CC',
                              background: done ? '#10B981' : 'white',
                              cursor: isBusy ? 'wait' : 'pointer',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              transition: 'all 0.2s',
                            }}
                          >
                            {done && <svg width="11" height="8" viewBox="0 0 11 8" fill="none">
                              <path d="M1 4L4 7L10 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>}
                          </button>

                          <span className="flex-1 text-sm" style={{
                            color: done ? '#10B981' : '#1A1A1A',
                            textDecoration: done ? 'line-through' : 'none',
                            textDecorationColor: '#10B981',
                            paddingTop: 2,
                          }}>
                            {task.area}
                          </span>

                          <div className="flex items-center gap-2 flex-wrap" style={{ flexShrink: 0, justifyContent: 'flex-end' }}>
                            <div className="flex items-center gap-1 text-xs font-semibold"
                              style={{ padding: '3px 9px', borderRadius: 12, background: 'rgba(4,57,89,0.08)', color: '#043959', whiteSpace: 'nowrap' }}>
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                              </svg>
                              {task.staff_count} req
                            </div>

                            {assigned.map(a => (
                              <div key={a.assignment_id} className="flex items-center gap-1" style={{ fontSize: 12 }}>
                                <div style={{ width: 7, height: 7, borderRadius: '50%', background: hkAvatarColor(a.staff_id), flexShrink: 0 }}/>
                                <span style={{ color: '#444', fontWeight: 500, whiteSpace: 'nowrap' }}>{a.staff_name}</span>
                                <button
                                  onClick={() => handleUnassign(a.assignment_id, task.id)}
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#CCC', padding: 0, lineHeight: 1, fontSize: 13 }}
                                  title="Remove"
                                >×</button>
                              </div>
                            ))}

                            {needed > 0 && <span style={{ fontSize: 11, color: '#F59E0B', fontWeight: 500, whiteSpace: 'nowrap' }}>{needed} more</span>}
                            {needed <= 0 && assigned.length > 0 && <span style={{ fontSize: 11, color: '#10B981', fontWeight: 500 }}>✓ Full</span>}

                            <HKStaffPicker task={task} date={date} allStaff={allStaff}
                              assigned={assigned} onAssign={handleAssign} onUnassign={handleUnassign}
                              blockAssignments={blockAssignments} blockTasks={block.tasks} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="text-center text-xs mt-8 pb-4" style={{ color: '#CCC' }}>
        {totalStaff} total staff positions across all tasks
      </div>
    </div>
  );
}

export default function ServicesPage() {
  const [activeTab, setActiveTab] = useState('all');
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const today = new Date().toISOString().split('T')[0];

  // Add Staff modal state
  const [showAddStaffModal, setShowAddStaffModal] = useState(false);
  const [showAllocationModal, setShowAllocationModal] = useState(false);
  const [allocationStatus, setAllocationStatus] = useState('present');
  const [allocationDate, setAllocationDate] = useState(today);
  const [editingStaffId, setEditingStaffId] = useState(null);
  const [addStaffSubmitting, setAddStaffSubmitting] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [aadharFile, setAadharFile] = useState(null);
  const [addStaffForm, setAddStaffForm] = useState({
    name: '', phone: '', staff_type: 'cleaning',
    work_start_time: '08:30', work_end_time: '16:15',
    working_days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    towers: [], floorsA: [], floorsB: [], securityPosts: [], cleaningSections: [],
  });

  // Staff Directory state
  const [allStaff, setAllStaff] = useState([]);
  const [allStaffLoading, setAllStaffLoading] = useState(false);
  const [directorySearch, setDirectorySearch] = useState('');
  const [directoryType, setDirectoryType] = useState('');
  const [expandedStaffId, setExpandedStaffId] = useState(null);

  // Attendance state
  const [attendance, setAttendance] = useState({});
  const [attendanceDate, setAttendanceDate] = useState(today);
  const [attendanceTypeFilter, setAttendanceTypeFilter] = useState('');
  const [attendanceStatusFilter, setAttendanceStatusFilter] = useState('');

  // Leave Management state
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [leaveForm, setLeaveForm] = useState({
    staffId: '',
    leaveType: 'Casual',
    fromDate: today,
    toDate: today,
    reason: '',
  });

  const [filters, setFilters] = useState({
    service_type: '',
    name: '',
    area: '',
    floor: '',
    cleaning_section: '',
    security_post: '',
    shift: '',
    date: today,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const hasFilters = filters.service_type || filters.area || filters.floor || filters.shift || filters.security_post;

      let staffRes;
      if (hasFilters) {
        const filterPayload = {};
        if (filters.service_type) filterPayload.staff_type = filters.service_type;
        if (filters.area) filterPayload.tower = filters.area;
        if (filters.floor) filterPayload.floor = filters.floor;
        if (filters.shift) filterPayload.shift_type = filters.shift;
        staffRes = await filterStaff(filterPayload);
      } else if (filters.date && filters.date !== today) {
        staffRes = await getRosterByDate(filters.date);
      } else {
        staffRes = await getTodayRoster();
      }

      let data = staffRes.data?.data || staffRes.data || [];
      data = Array.isArray(data) ? data : [];
      if (filters.name) {
        data = data.filter((s) => s.name === filters.name);
      }
      if (filters.cleaning_section) {
        data = data.filter((s) => s.assignments?.some((a) => a.cleaning_section === filters.cleaning_section));
      }
      if (filters.security_post) {
        data = data.filter((s) => s.assignments?.some((a) => a.security_post === filters.security_post));
      }
      setStaff(data);
    } catch (err) {
      console.error('Failed to load staff roster', err);
    } finally {
      setLoading(false);
    }
  }, [filters, today]);


  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchAllStaff = useCallback(() => {
    setAllStaffLoading(true);
    getStaff({ all: true })
      .then((res) => {
        const data = res.data || res || [];
        setAllStaff(Array.isArray(data) ? data : []);
      })
      .catch((err) => console.error('Failed to load all staff', err))
      .finally(() => setAllStaffLoading(false));
  }, []);

  // Fetch all staff for directory, attendance, leave, reports tabs
  useEffect(() => {
    if (activeTab !== 'all' && allStaff.length === 0 && !allStaffLoading) {
      fetchAllStaff();
    }
  }, [activeTab, allStaff.length, allStaffLoading, fetchAllStaff]);

  // Shared effective date for attendance (sync both tabs)
  const effectiveDate = activeTab === 'attendance' ? attendanceDate : (filters.date || today);

  // Load attendance from API when date changes or staff loads
  useEffect(() => {
    const combined = [...staff, ...allStaff];
    if (combined.length > 0) {
      getAttendance(effectiveDate)
        .then((res) => {
          const saved = res.data || {};
          const updated = {};
          combined.forEach((s) => {
            updated[s.id] = saved[s.id] || (s.is_active ? 'present' : 'absent');
          });
          setAttendance(updated);
        })
        .catch(() => {
          const updated = {};
          combined.forEach((s) => {
            updated[s.id] = s.is_active ? 'present' : 'absent';
          });
          setAttendance(updated);
        });
    }
  }, [allStaff, staff, effectiveDate]);

  // Filtered staff for directory
  const filteredDirectory = useMemo(() => {
    let result = allStaff;
    if (directorySearch) {
      const q = directorySearch.toLowerCase();
      result = result.filter((s) => s.name?.toLowerCase().includes(q));
    }
    if (directoryType) {
      result = result.filter((s) => s.staff_type === directoryType);
    }
    return result;
  }, [allStaff, directorySearch, directoryType]);

  // Attendance stats
  const attendanceValues = Object.values(attendance);
  const attendanceStats = {
    total: allStaff.length,
    present: attendanceValues.filter((v) => v === 'present').length,
    absent: attendanceValues.filter((v) => v === 'absent').length,
    leave: attendanceValues.filter((v) => v === 'leave').length,
  };

  // Leave stats
  const leaveStats = useMemo(() => ({
    total: leaveRequests.length,
    pending: leaveRequests.filter((r) => r.status === 'Pending').length,
    approved: leaveRequests.filter((r) => r.status === 'Approved').length,
    rejected: leaveRequests.filter((r) => r.status === 'Rejected').length,
  }), [leaveRequests]);

  // Reports data
  const reportData = useMemo(() => {
    const cleaningCount = allStaff.filter((s) => s.staff_type === 'cleaning').length;
    const secCount = allStaff.filter((s) => s.staff_type === 'security').length;

    const shifts = { day: 0, evening: 0, night: 0, unknown: 0 };
    allStaff.forEach((s) => {
      const startHour = s.work_start_time ? parseInt(s.work_start_time.split(':')[0], 10) : -1;
      if (startHour >= 5 && startHour < 14) {
        shifts.day++;
      } else if (startHour >= 14 && startHour < 21) {
        shifts.evening++;
      } else if (startHour >= 21 || (startHour >= 0 && startHour < 5)) {
        shifts.night++;
      } else {
        shifts.unknown++;
      }
    });

    const towerAllocation = {};
    allStaff.forEach((s) => {
      if (s.assignments) {
        s.assignments.forEach((a) => {
          if (a.tower) {
            towerAllocation[a.tower] = (towerAllocation[a.tower] || 0) + 1;
          }
          if (a.security_post) {
            towerAllocation[a.security_post] = (towerAllocation[a.security_post] || 0) + 1;
          }
        });
      }
    });

    const dayCoverage = {};
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    dayNames.forEach((d) => { dayCoverage[d] = 0; });
    allStaff.forEach((s) => {
      if (s.working_days) {
        const days = typeof s.working_days === 'string' ? JSON.parse(s.working_days) : s.working_days;
        if (Array.isArray(days)) {
          days.forEach((d) => {
            const capitalized = d.charAt(0).toUpperCase() + d.slice(1).toLowerCase();
            if (dayCoverage[capitalized] !== undefined) {
              dayCoverage[capitalized]++;
            }
          });
        }
      }
    });

    return { cleaningCount, secCount, shifts, towerAllocation, dayCoverage };
  }, [allStaff]);

  // Add Staff form handlers
  const handleAddStaffChange = (e) => setAddStaffForm({ ...addStaffForm, [e.target.name]: e.target.value });

  const toggleAddStaffDay = (day) => {
    const days = addStaffForm.working_days.includes(day)
      ? addStaffForm.working_days.filter((d) => d !== day)
      : [...addStaffForm.working_days, day];
    setAddStaffForm({ ...addStaffForm, working_days: days });
  };

  const toggleAddStaffArrayItem = (field, item) => {
    const arr = addStaffForm[field];
    const updated = arr.includes(item) ? arr.filter((v) => v !== item) : [...arr, item];
    setAddStaffForm({ ...addStaffForm, [field]: updated });
  };

  const resetAddStaffForm = () => {
    setAddStaffForm({
      name: '', phone: '', staff_type: 'cleaning',
      work_start_time: '08:30', work_end_time: '16:15',
      working_days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      towers: [], floorsA: [], floorsB: [], securityPosts: [], cleaningSections: [],
    });
    setPhotoFile(null);
    setPhotoPreview(null);
    setAadharFile(null);
  };

  const handleAddStaffSubmit = async (e) => {
    e.preventDefault();
    setAddStaffSubmitting(true);
    try {
      const assignments = [];
      if (addStaffForm.staff_type === 'cleaning') {
        if (addStaffForm.towers.includes('Tower A')) {
          addStaffForm.floorsA.forEach((floor) => {
            assignments.push({ assignment_type: 'tower_floor', tower: 'Tower A', floor, security_post: null, cleaning_section: null });
          });
        }
        if (addStaffForm.towers.includes('Tower B')) {
          addStaffForm.floorsB.forEach((floor) => {
            assignments.push({ assignment_type: 'tower_floor', tower: 'Tower B', floor, security_post: null, cleaning_section: null });
          });
        }
        addStaffForm.cleaningSections.forEach((section) => {
          assignments.push({ assignment_type: 'cleaning_section', tower: null, floor: null, security_post: null, cleaning_section: section });
        });
      } else {
        addStaffForm.securityPosts.forEach((post) => {
          assignments.push({ assignment_type: 'security_post', tower: null, floor: null, security_post: post });
        });
      }

      // Upload photo and aadhar if selected
      let photoPath = null;
      let aadharPath = null;
      if (photoFile) {
        const photoRes = await uploadStaffPhoto(photoFile);
        photoPath = photoRes.data.path;
      }
      if (aadharFile) {
        const aadharRes = await uploadStaffAadhar(aadharFile);
        aadharPath = aadharRes.data.path;
      }

      const isAbsent = showAllocationModal && allocationStatus === 'absent';
      const payload = {
        name: addStaffForm.name,
        phone: addStaffForm.phone || null,
        staff_type: addStaffForm.staff_type,
        shift_type: 'Day',
        work_start_time: addStaffForm.work_start_time || '08:30',
        work_end_time: addStaffForm.work_end_time || '16:15',
        working_days: addStaffForm.working_days,
        is_active: true,
        assignments: assignments.length > 0 ? assignments : [],
        ...(photoPath && { photo_path: photoPath }),
        ...(aadharPath && { aadhar_path: aadharPath }),
      };

      if (editingStaffId) {
        await updateStaff(editingStaffId, payload);
        // Save attendance status separately
        if (showAllocationModal) {
          const dateToSave = allocationDate || today;
          await saveAttendance({
            date: dateToSave,
            attendance: [{ staff_member_id: editingStaffId, status: isAbsent ? 'absent' : 'present' }],
          });
          setAttendance((prev) => ({ ...prev, [editingStaffId]: isAbsent ? 'absent' : 'present' }));
        }
      } else {
        await createStaff(payload);
      }
      setShowAddStaffModal(false);
      setShowAllocationModal(false);
      setAllocationStatus('present');
      setAllocationDate(today);
      setEditingStaffId(null);
      resetAddStaffForm();
      fetchData();
      fetchAllStaff();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to create staff');
    } finally {
      setAddStaffSubmitting(false);
    }
  };

  const handleDeleteStaff = async (id) => {
    if (!window.confirm('Are you sure you want to remove this staff member?')) return;
    try {
      await deleteStaff(id);
      fetchData();
      fetchAllStaff();
    } catch (err) {
      alert('Failed to delete staff');
    }
  };

  const handleLeaveSubmit = () => {
    if (!leaveForm.staffId || !leaveForm.fromDate || !leaveForm.toDate) return;
    const staffMember = allStaff.find((s) => String(s.id) === String(leaveForm.staffId));
    const from = new Date(leaveForm.fromDate);
    const to = new Date(leaveForm.toDate);
    const days = Math.max(1, Math.ceil((to - from) / (1000 * 60 * 60 * 24)) + 1);
    setLeaveRequests((prev) => [
      ...prev,
      {
        id: Date.now(),
        staffName: staffMember?.name || 'Unknown',
        staffId: leaveForm.staffId,
        leaveType: leaveForm.leaveType,
        fromDate: leaveForm.fromDate,
        toDate: leaveForm.toDate,
        days,
        reason: leaveForm.reason,
        status: 'Pending',
      },
    ]);
    setLeaveForm({ staffId: '', leaveType: 'Casual', fromDate: today, toDate: today, reason: '' });
    setShowLeaveModal(false);
  };

  const updateLeaveStatus = (id, status) => {
    setLeaveRequests((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({ service_type: '', name: '', area: '', floor: '', cleaning_section: '', security_post: '', shift: '', date: today });
  };

  const applyFilters = () => {
    fetchData();
  };


  const getFloorOptions = () => {
    switch (filters.area) {
      case 'Tower A': return towerAFloors;
      case 'Tower B': return towerBFloors;
      default: return [];
    }
  };

  const staffNames = [...new Set(staff.map((s) => s.name))].sort();
  const isCleaning = filters.service_type === 'cleaning';
  const isSecurity = filters.service_type === 'security';

  return (
    <AppLayout title="Work Management" breadcrumbs="Work Management">
      {/* Sub Navigation */}
      <div
        className="flex overflow-x-auto -mx-8 -mt-8 mb-0"
        style={{
          background: 'white',
          borderBottom: '1px solid #E5E5E0',
          padding: '0 40px',
          gap: '32px',
        }}
      >
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className="whitespace-nowrap bg-transparent border-0 cursor-pointer transition-all"
            style={{
              padding: '16px 4px',
              fontSize: '14px',
              fontWeight: 500,
              fontFamily: "'Lexend', sans-serif",
              color: activeTab === tab.key ? '#043959' : '#666666',
              borderBottom: `3px solid ${activeTab === tab.key ? '#043959' : 'transparent'}`,
            }}
            onMouseEnter={(e) => {
              if (activeTab !== tab.key) {
                e.currentTarget.style.color = '#043959';
                e.currentTarget.style.borderBottomColor = '#F4A460';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== tab.key) {
                e.currentTarget.style.color = '#666666';
                e.currentTarget.style.borderBottomColor = 'transparent';
              }
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div style={{ padding: '32px 0' }}>
        {activeTab === 'all' && (<HousekeepingTab />)}

        {activeTab === 'security' && (<SecurityScheduleTab />)}

        {/* ====== STAFF DIRECTORY TAB ====== */}
        {activeTab === 'staff' && (
          <div>
            <div className="flex justify-between items-center" style={{ marginBottom: '20px' }}>
              <h3 className="text-lg font-semibold" style={{ color: '#1A1A1A' }}>Staff Directory</h3>
              <button
                onClick={() => { resetAddStaffForm(); setEditingStaffId(null); setShowAddStaffModal(true); }}
                className="inline-flex items-center gap-2 text-white text-sm font-medium rounded-lg transition-all hover:opacity-90 border-0 cursor-pointer"
                style={{ padding: '10px 20px', background: '#043959', fontFamily: "'Lexend', sans-serif" }}
              >
                <span>+</span> Add Staff
              </button>
            </div>

            {/* Search, Filter & Staff Select */}
            <div className="flex flex-wrap gap-3" style={{ marginBottom: '24px' }}>
              <input
                type="text"
                placeholder="Search by name..."
                value={directorySearch}
                onChange={(e) => setDirectorySearch(e.target.value)}
                style={{
                  padding: '8px 14px', border: '1px solid #E5E5E0', borderRadius: '8px',
                  fontSize: '13px', fontFamily: "'Lexend', sans-serif", outline: 'none', minWidth: '220px',
                }}
              />
              <select
                value={directoryType}
                onChange={(e) => setDirectoryType(e.target.value)}
                style={{
                  padding: '8px 14px', border: '1px solid #E5E5E0', borderRadius: '8px',
                  fontSize: '13px', fontFamily: "'Lexend', sans-serif", background: 'white', outline: 'none',
                }}
              >
                <option value="">All Types</option>
                <option value="cleaning">Cleaning</option>
                <option value="security">Security</option>
              </select>
              <select
                value={expandedStaffId || ''}
                onChange={(e) => setExpandedStaffId(e.target.value ? Number(e.target.value) : null)}
                style={{
                  padding: '8px 14px', border: '1px solid #E5E5E0', borderRadius: '8px',
                  fontSize: '13px', fontFamily: "'Lexend', sans-serif", background: 'white', outline: 'none', minWidth: '200px',
                }}
              >
                <option value="">Select Staff Member</option>
                {filteredDirectory.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>

            {allStaffLoading ? (
              <div className="text-center text-sm" style={{ padding: '60px 16px', color: '#999999' }}>Loading staff...</div>
            ) : filteredDirectory.length === 0 ? (
              <div className="text-center text-sm" style={{ padding: '60px 16px', color: '#999999' }}>No staff found</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                {filteredDirectory.map((member) => {
                  const summary = getAssignmentSummary(member);
                  return (
                    <div key={member.id} className="bg-white" style={{ border: '1px solid #E5E5E0', borderRadius: '12px', padding: '20px' }}>
                      {/* Staff Header */}
                      <div className="flex items-center gap-4" style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid #F0F0EB' }}>
                        {member.photo_path ? (
                          <img
                            src={`${(process.env.REACT_APP_API_URL || 'http://tgcaoa-backend.test/api').replace('/api', '')}/storage/${member.photo_path}`}
                            alt={member.name}
                            style={{ width: '56px', height: '56px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                          />
                        ) : (
                          <div
                            className="flex items-center justify-center text-white font-semibold"
                            style={{
                              width: '56px', height: '56px', borderRadius: '50%', fontSize: '20px', flexShrink: 0,
                              background: 'linear-gradient(135deg, #043959, #F4A460)',
                            }}
                          >
                            {getInitials(member.name)}
                          </div>
                        )}
                        <div style={{ flex: 1 }}>
                          <h4 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px', color: '#1A1A1A' }}>{member.name}</h4>
                          <p style={{ fontSize: '13px', color: '#999999' }}>
                            {member.staff_type === 'cleaning' ? 'Cleaning Staff' : 'Security Staff'}
                          </p>
                        </div>
                        {member.aadhar_path && (
                          <div
                            title="View Aadhaar"
                            onClick={() => window.open(`${(process.env.REACT_APP_API_URL || 'http://tgcaoa-backend.test/api').replace('/api', '')}/storage/${member.aadhar_path}`, '_blank')}
                            className="flex items-center justify-center cursor-pointer"
                            style={{
                              width: '40px', height: '40px', borderRadius: '8px', flexShrink: 0,
                              background: '#F0F0EB', transition: 'all 0.2s',
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = '#E5E5E0'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = '#F0F0EB'; }}
                          >
                            <img src="/aadhaar-icon.svg" alt="Aadhaar" style={{ width: '28px', height: '28px', objectFit: 'contain' }} />
                          </div>
                        )}
                      </div>

                      {/* Working Days */}
                      <div style={{ background: '#FAFAF8', padding: '12px', borderRadius: '8px', marginBottom: '12px' }}>
                        <div style={{ fontSize: '12px', fontWeight: 500, color: '#666666', marginBottom: '4px' }}>Working Days</div>
                        <div className="flex flex-wrap gap-1" style={{ marginTop: '4px' }}>
                          {allDays.map((day) => {
                            const isActive = member.working_days?.includes(day);
                            return (
                              <span
                                key={day}
                                className="inline-flex items-center justify-center"
                                style={{
                                  minWidth: '36px', padding: '4px 8px', borderRadius: '6px',
                                  fontSize: '11px', fontWeight: 600,
                                  background: isActive ? '#043959' : 'white',
                                  color: isActive ? 'white' : '#999999',
                                  border: isActive ? '1.5px solid #043959' : '1.5px solid #E5E5E0',
                                }}
                              >
                                {dayLabels[day]}
                              </span>
                            );
                          })}
                        </div>
                      </div>

                      {/* Working Hours */}
                      <div style={{ background: '#FAFAF8', padding: '12px', borderRadius: '8px', marginBottom: '12px' }}>
                        <div style={{ fontSize: '12px', fontWeight: 500, color: '#666666', marginBottom: '4px' }}>Working Hours</div>
                        <div style={{ fontSize: '14px', fontWeight: 500, color: '#1A1A1A' }}>
                          {formatTime12(member.work_start_time)} - {formatTime12(member.work_end_time)}
                        </div>
                      </div>

                      {/* Contact */}
                      <div style={{ background: '#FAFAF8', padding: '12px', borderRadius: '8px', marginBottom: '0' }}>
                        <div style={{ fontSize: '12px', fontWeight: 500, color: '#666666', marginBottom: '4px' }}>Contact</div>
                        <div style={{ fontSize: '14px', fontWeight: 500, color: '#1A1A1A' }}>
                          {member.phone || '-'}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2" style={{ marginTop: '16px' }}>
                        <button
                          onClick={() => {
                            setEditingStaffId(member.id);
                            setAddStaffForm({
                              name: member.name,
                              phone: member.phone || '',
                              staff_type: member.staff_type,
                              work_start_time: member.work_start_time ? member.work_start_time.slice(0, 5) : '08:30',
                              work_end_time: member.work_end_time ? member.work_end_time.slice(0, 5) : '16:15',
                              working_days: member.working_days || [],
                              towers: [], floorsA: [], floorsB: [], securityPosts: [], cleaningSections: [],
                            });
                            setShowAddStaffModal(true);
                          }}
                          className="text-sm font-medium rounded-lg transition-all cursor-pointer"
                          style={{
                            flex: 1, padding: '8px 12px',
                            background: '#FAFAF8', color: '#043959',
                            border: '1px solid #E5E5E0',
                            fontFamily: "'Lexend', sans-serif",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = '#043959';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = '#E5E5E0';
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteStaff(member.id)}
                          className="text-sm font-medium rounded-lg transition-all cursor-pointer"
                          style={{
                            flex: 1, padding: '8px 12px',
                            background: '#FAFAF8', color: '#1A1A1A',
                            border: '1px solid #E5E5E0',
                            fontFamily: "'Lexend', sans-serif",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = '#EF4444';
                            e.currentTarget.style.color = '#EF4444';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = '#E5E5E0';
                            e.currentTarget.style.color = '#1A1A1A';
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ====== ATTENDANCE TRACKER TAB ====== */}
        {activeTab === 'attendance' && (
          <div>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" style={{ marginBottom: '24px' }}>
              <h3 className="text-lg font-semibold" style={{ color: '#1A1A1A' }}>Attendance Tracker</h3>
              <div className="flex flex-wrap items-center gap-3">
                <input
                  type="date"
                  value={attendanceDate}
                  onChange={(e) => setAttendanceDate(e.target.value)}
                  style={{
                    padding: '8px 14px', border: '1px solid #E5E5E0', borderRadius: '8px',
                    fontSize: '13px', fontFamily: "'Lexend', sans-serif", outline: 'none',
                  }}
                />
                <select
                  value={attendanceTypeFilter}
                  onChange={(e) => setAttendanceTypeFilter(e.target.value)}
                  style={{
                    padding: '8px 14px', border: '1px solid #E5E5E0', borderRadius: '8px',
                    fontSize: '13px', fontFamily: "'Lexend', sans-serif", outline: 'none', background: 'white', color: '#1A1A1A',
                  }}
                >
                  <option value="">All Types</option>
                  <option value="cleaning">Cleaning</option>
                  <option value="security">Security</option>
                </select>
                <select
                  value={attendanceStatusFilter}
                  onChange={(e) => setAttendanceStatusFilter(e.target.value)}
                  style={{
                    padding: '8px 14px', border: '1px solid #E5E5E0', borderRadius: '8px',
                    fontSize: '13px', fontFamily: "'Lexend', sans-serif", outline: 'none', background: 'white', color: '#1A1A1A',
                  }}
                >
                  <option value="">All Status</option>
                  <option value="present">Present</option>
                  <option value="absent">Absent</option>
                  <option value="leave">On Leave</option>
                </select>
                {(attendanceTypeFilter || attendanceStatusFilter) && (
                  <button
                    onClick={() => { setAttendanceTypeFilter(''); setAttendanceStatusFilter(''); }}
                    className="text-xs font-medium cursor-pointer border-0 bg-transparent"
                    style={{ color: '#EF4444', fontFamily: "'Lexend', sans-serif", padding: '8px 4px' }}
                  >
                    Clear
                  </button>
                )}
                <button
                  onClick={async () => {
                    try {
                      const entries = Object.entries(attendance).map(([id, status]) => ({
                        staff_member_id: Number(id),
                        status,
                      }));
                      await saveAttendance({ date: attendanceDate, attendance: entries });
                      alert('Attendance saved successfully!');
                    } catch (err) {
                      console.error('Failed to save attendance', err);
                      alert('Failed to save attendance');
                    }
                  }}
                  className="inline-flex items-center gap-2 text-white text-sm font-medium rounded-lg transition-all hover:opacity-90 border-0 cursor-pointer"
                  style={{ padding: '8px 20px', background: '#043959', fontFamily: "'Lexend', sans-serif" }}
                >
                  Save Attendance
                </button>
              </div>
            </div>

            {/* Summary Bar */}
            <div className="grid grid-cols-2 md:grid-cols-4" style={{ gap: '12px', marginBottom: '24px' }}>
              {[
                { label: 'Total', value: attendanceStats.total, bg: 'rgba(4,57,89,0.1)', color: '#043959' },
                { label: 'Present', value: attendanceStats.present, bg: 'rgba(16,185,129,0.1)', color: '#10B981' },
                { label: 'Absent', value: attendanceStats.absent, bg: 'rgba(239,68,68,0.1)', color: '#EF4444' },
                { label: 'On Leave', value: attendanceStats.leave, bg: 'rgba(245,158,11,0.1)', color: '#F59E0B' },
              ].map((s) => (
                <div key={s.label} className="bg-white text-center" style={{ padding: '16px', borderRadius: '10px', border: '1px solid #E5E5E0' }}>
                  <div className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</div>
                  <div className="text-xs font-medium" style={{ color: '#666666', marginTop: '2px' }}>{s.label}</div>
                </div>
              ))}
            </div>

            {allStaffLoading ? (
              <div className="text-center text-sm" style={{ padding: '60px 16px', color: '#999999' }}>Loading staff...</div>
            ) : (
              <div className="bg-white overflow-hidden" style={{ borderRadius: '12px', border: '1px solid #E5E5E0' }}>
                <div className="overflow-x-auto">
                  <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#FAFAF8', borderBottom: '2px solid #E5E5E0' }}>
                        {['Staff Name', 'Type', 'Mark Attendance'].map((h) => (
                          <th key={h} className="text-left uppercase" style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, letterSpacing: '0.5px', color: '#666666' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {allStaff
                        .filter((m) => !attendanceTypeFilter || m.staff_type === attendanceTypeFilter)
                        .filter((m) => !attendanceStatusFilter || (attendance[m.id] || (m.is_active ? 'present' : 'absent')) === attendanceStatusFilter)
                        .map((member) => {
                        const status = attendance[member.id] || 'absent';
                        const statusColors = {
                          present: { bg: 'rgba(16,185,129,0.1)', color: '#10B981', border: '#10B981' },
                          absent: { bg: 'rgba(239,68,68,0.1)', color: '#EF4444', border: '#EF4444' },
                          leave: { bg: 'rgba(245,158,11,0.1)', color: '#F59E0B', border: '#F59E0B' },
                        };
                        return (
                          <tr key={member.id} style={{ borderBottom: '1px solid #F0F0EB' }}>
                            <td style={{ padding: '12px 16px' }}>
                              <div className="flex items-center gap-3">
                                <div
                                  className="flex items-center justify-center text-white font-semibold"
                                  style={{
                                    width: '32px', height: '32px', borderRadius: '50%', fontSize: '11px',
                                    background: 'linear-gradient(135deg, #043959, #F4A460)', flexShrink: 0,
                                  }}
                                >
                                  {getInitials(member.name)}
                                </div>
                                <span style={{ fontSize: '13px', fontWeight: 500, color: '#1A1A1A' }}>{member.name}</span>
                              </div>
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              <span style={{
                                fontSize: '12px', fontWeight: 500, padding: '3px 8px', borderRadius: '4px',
                                background: member.staff_type === 'security' ? 'rgba(59,130,246,0.1)' : 'rgba(16,185,129,0.1)',
                                color: member.staff_type === 'security' ? '#3B82F6' : '#10B981',
                              }}>
                                {member.staff_type === 'security' ? 'Security' : 'Cleaning'}
                              </span>
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              <div className="flex gap-2">
                                {['present', 'absent', 'leave'].map((s) => (
                                  <button
                                    key={s}
                                    onClick={() => setAttendance((prev) => ({ ...prev, [member.id]: s }))}
                                    className="text-xs font-medium rounded-md cursor-pointer transition-all"
                                    style={{
                                      padding: '5px 12px',
                                      fontFamily: "'Lexend', sans-serif",
                                      background: status === s ? statusColors[s].bg : 'white',
                                      color: status === s ? statusColors[s].color : '#666666',
                                      border: `1px solid ${status === s ? statusColors[s].border : '#E5E5E0'}`,
                                    }}
                                  >
                                    {s === 'present' ? 'Present' : s === 'absent' ? 'Absent' : 'Leave'}
                                  </button>
                                ))}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ====== LEAVE MANAGEMENT TAB ====== */}
        {activeTab === 'leave' && (
          <div>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" style={{ marginBottom: '24px' }}>
              <h3 className="text-lg font-semibold" style={{ color: '#1A1A1A' }}>Leave Management</h3>
              <button
                onClick={() => setShowLeaveModal(true)}
                className="text-sm font-medium rounded-lg text-white cursor-pointer border-0"
                style={{ padding: '8px 20px', background: '#043959', fontFamily: "'Lexend', sans-serif" }}
              >
                + Apply Leave
              </button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4" style={{ gap: '12px', marginBottom: '24px' }}>
              {[
                { label: 'Total Requests', value: leaveStats.total, bg: 'rgba(4,57,89,0.1)', color: '#043959' },
                { label: 'Pending', value: leaveStats.pending, bg: 'rgba(245,158,11,0.1)', color: '#F59E0B' },
                { label: 'Approved', value: leaveStats.approved, bg: 'rgba(16,185,129,0.1)', color: '#10B981' },
                { label: 'Rejected', value: leaveStats.rejected, bg: 'rgba(239,68,68,0.1)', color: '#EF4444' },
              ].map((s) => (
                <div key={s.label} className="bg-white text-center" style={{ padding: '16px', borderRadius: '10px', border: '1px solid #E5E5E0' }}>
                  <div className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</div>
                  <div className="text-xs font-medium" style={{ color: '#666666', marginTop: '2px' }}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* Leave Requests Table */}
            {leaveRequests.length === 0 ? (
              <div className="bg-white text-center text-sm" style={{ padding: '60px 16px', color: '#999999', borderRadius: '12px', border: '1px solid #E5E5E0' }}>
                No leave requests yet. Click "Apply Leave" to create one.
              </div>
            ) : (
              <div className="bg-white overflow-hidden" style={{ borderRadius: '12px', border: '1px solid #E5E5E0' }}>
                <div className="overflow-x-auto">
                  <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#FAFAF8', borderBottom: '2px solid #E5E5E0' }}>
                        {['Staff Name', 'Leave Type', 'From', 'To', 'Days', 'Reason', 'Status', 'Actions'].map((h) => (
                          <th key={h} className="text-left uppercase" style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, letterSpacing: '0.5px', color: '#666666' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {leaveRequests.map((req) => (
                        <tr key={req.id} style={{ borderBottom: '1px solid #F0F0EB' }}>
                          <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 500, color: '#1A1A1A' }}>{req.staffName}</td>
                          <td style={{ padding: '12px 16px', fontSize: '13px', color: '#1A1A1A' }}>{req.leaveType}</td>
                          <td style={{ padding: '12px 16px', fontSize: '13px', color: '#666666' }}>{new Date(req.fromDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</td>
                          <td style={{ padding: '12px 16px', fontSize: '13px', color: '#666666' }}>{new Date(req.toDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</td>
                          <td style={{ padding: '12px 16px', fontSize: '13px', color: '#1A1A1A' }}>{req.days}</td>
                          <td style={{ padding: '12px 16px', fontSize: '13px', color: '#666666', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{req.reason || '-'}</td>
                          <td style={{ padding: '12px 16px' }}>
                            <span style={{
                              padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 500,
                              background: req.status === 'Approved' ? 'rgba(16,185,129,0.1)' : req.status === 'Rejected' ? 'rgba(239,68,68,0.1)' : 'rgba(245,158,11,0.1)',
                              color: req.status === 'Approved' ? '#10B981' : req.status === 'Rejected' ? '#EF4444' : '#F59E0B',
                            }}>
                              {req.status}
                            </span>
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            {req.status === 'Pending' && (
                              <div className="flex gap-2">
                                <button
                                  onClick={() => updateLeaveStatus(req.id, 'Approved')}
                                  className="text-xs font-medium rounded-md cursor-pointer"
                                  style={{ padding: '4px 10px', background: 'rgba(16,185,129,0.1)', color: '#10B981', border: '1px solid #10B981', fontFamily: "'Lexend', sans-serif" }}
                                >
                                  Approve
                                </button>
                                <button
                                  onClick={() => updateLeaveStatus(req.id, 'Rejected')}
                                  className="text-xs font-medium rounded-md cursor-pointer"
                                  style={{ padding: '4px 10px', background: 'rgba(239,68,68,0.1)', color: '#EF4444', border: '1px solid #EF4444', fontFamily: "'Lexend', sans-serif" }}
                                >
                                  Reject
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Leave Modal */}
            {showLeaveModal && (
              <div
                className="fixed inset-0 flex items-center justify-center"
                style={{ background: 'rgba(0,0,0,0.4)', zIndex: 1000 }}
                onClick={(e) => { if (e.target === e.currentTarget) setShowLeaveModal(false); }}
              >
                <div className="bg-white" style={{ borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '480px', margin: '0 16px' }}>
                  <h4 style={{ fontSize: '16px', fontWeight: 600, color: '#1A1A1A', marginBottom: '20px' }}>Apply for Leave</h4>
                  <div className="flex flex-col" style={{ gap: '14px' }}>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 500, color: '#666666', display: 'block', marginBottom: '4px' }}>Staff Member</label>
                      <select
                        value={leaveForm.staffId}
                        onChange={(e) => setLeaveForm((prev) => ({ ...prev, staffId: e.target.value }))}
                        className="w-full"
                        style={{ padding: '8px 10px', border: '1px solid #E5E5E0', borderRadius: '8px', fontSize: '13px', fontFamily: "'Lexend', sans-serif", background: 'white', outline: 'none' }}
                      >
                        <option value="">Select staff...</option>
                        {allStaff.map((s) => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 500, color: '#666666', display: 'block', marginBottom: '4px' }}>Leave Type</label>
                      <select
                        value={leaveForm.leaveType}
                        onChange={(e) => setLeaveForm((prev) => ({ ...prev, leaveType: e.target.value }))}
                        className="w-full"
                        style={{ padding: '8px 10px', border: '1px solid #E5E5E0', borderRadius: '8px', fontSize: '13px', fontFamily: "'Lexend', sans-serif", background: 'white', outline: 'none' }}
                      >
                        <option value="Casual">Casual Leave</option>
                        <option value="Sick">Sick Leave</option>
                        <option value="Earned">Earned Leave</option>
                      </select>
                    </div>
                    <div className="grid grid-cols-2" style={{ gap: '12px' }}>
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 500, color: '#666666', display: 'block', marginBottom: '4px' }}>From Date</label>
                        <input
                          type="date"
                          value={leaveForm.fromDate}
                          onChange={(e) => setLeaveForm((prev) => ({ ...prev, fromDate: e.target.value }))}
                          className="w-full"
                          style={{ padding: '8px 10px', border: '1px solid #E5E5E0', borderRadius: '8px', fontSize: '13px', fontFamily: "'Lexend', sans-serif", outline: 'none' }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 500, color: '#666666', display: 'block', marginBottom: '4px' }}>To Date</label>
                        <input
                          type="date"
                          value={leaveForm.toDate}
                          onChange={(e) => setLeaveForm((prev) => ({ ...prev, toDate: e.target.value }))}
                          className="w-full"
                          style={{ padding: '8px 10px', border: '1px solid #E5E5E0', borderRadius: '8px', fontSize: '13px', fontFamily: "'Lexend', sans-serif", outline: 'none' }}
                        />
                      </div>
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 500, color: '#666666', display: 'block', marginBottom: '4px' }}>Reason</label>
                      <textarea
                        value={leaveForm.reason}
                        onChange={(e) => setLeaveForm((prev) => ({ ...prev, reason: e.target.value }))}
                        placeholder="Optional reason..."
                        rows={3}
                        className="w-full"
                        style={{ padding: '8px 10px', border: '1px solid #E5E5E0', borderRadius: '8px', fontSize: '13px', fontFamily: "'Lexend', sans-serif", outline: 'none', resize: 'vertical' }}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2" style={{ marginTop: '20px' }}>
                    <button
                      onClick={() => setShowLeaveModal(false)}
                      className="text-sm font-medium rounded-lg cursor-pointer"
                      style={{ padding: '8px 16px', background: '#FAFAF8', color: '#666666', border: '1px solid #E5E5E0', fontFamily: "'Lexend', sans-serif" }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleLeaveSubmit}
                      className="text-sm font-medium rounded-lg text-white cursor-pointer border-0"
                      style={{ padding: '8px 20px', background: '#043959', fontFamily: "'Lexend', sans-serif" }}
                    >
                      Submit
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ====== REPORTS TAB ====== */}
        {activeTab === 'reports' && (
          <div>
            <div style={{ marginBottom: '24px' }}>
              <h3 className="text-lg font-semibold" style={{ color: '#1A1A1A' }}>Reports & Overview</h3>
            </div>

            {allStaffLoading ? (
              <div className="text-center text-sm" style={{ padding: '60px 16px', color: '#999999' }}>Loading data...</div>
            ) : (
              <>
                {/* Staff by Type */}
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#666666', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>Staff Count by Type</div>
                  <div className="grid grid-cols-2 md:grid-cols-3" style={{ gap: '12px' }}>
                    <div className="bg-white" style={{ padding: '20px', borderRadius: '12px', border: '1px solid #E5E5E0' }}>
                      <div className="text-3xl font-bold" style={{ color: '#10B981' }}>{reportData.cleaningCount}</div>
                      <div className="text-xs font-medium" style={{ color: '#666666', marginTop: '4px' }}>Cleaning Staff</div>
                    </div>
                    <div className="bg-white" style={{ padding: '20px', borderRadius: '12px', border: '1px solid #E5E5E0' }}>
                      <div className="text-3xl font-bold" style={{ color: '#3B82F6' }}>{reportData.secCount}</div>
                      <div className="text-xs font-medium" style={{ color: '#666666', marginTop: '4px' }}>Security Staff</div>
                    </div>
                    <div className="bg-white" style={{ padding: '20px', borderRadius: '12px', border: '1px solid #E5E5E0' }}>
                      <div className="text-3xl font-bold" style={{ color: '#043959' }}>{allStaff.length}</div>
                      <div className="text-xs font-medium" style={{ color: '#666666', marginTop: '4px' }}>Total Staff</div>
                    </div>
                  </div>
                </div>

                {/* Shift Distribution */}
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#666666', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>Shift Distribution</div>
                  <div className="grid grid-cols-2 md:grid-cols-3" style={{ gap: '12px' }}>
                    {[
                      { label: 'Day Shift', value: reportData.shifts.day, color: '#F59E0B' },
                      { label: 'Evening Shift', value: reportData.shifts.evening, color: '#F4A460' },
                      { label: 'Night Shift', value: reportData.shifts.night, color: '#6366F1' },
                    ].map((s) => (
                      <div key={s.label} className="bg-white" style={{ padding: '20px', borderRadius: '12px', border: '1px solid #E5E5E0' }}>
                        <div className="text-3xl font-bold" style={{ color: s.color }}>{s.value}</div>
                        <div className="text-xs font-medium" style={{ color: '#666666', marginTop: '4px' }}>{s.label}</div>
                        <div style={{ marginTop: '8px', background: '#F0F0EB', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
                          <div style={{ width: allStaff.length > 0 ? `${(s.value / allStaff.length) * 100}%` : '0%', height: '100%', background: s.color, borderRadius: '4px' }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Tower/Post Allocation */}
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#666666', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>Staff Allocation by Area</div>
                  {Object.keys(reportData.towerAllocation).length === 0 ? (
                    <div className="bg-white text-center text-sm" style={{ padding: '32px', color: '#999999', borderRadius: '12px', border: '1px solid #E5E5E0' }}>No allocation data</div>
                  ) : (
                    <div className="bg-white overflow-hidden" style={{ borderRadius: '12px', border: '1px solid #E5E5E0' }}>
                      <div className="overflow-x-auto">
                        <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ background: '#FAFAF8', borderBottom: '2px solid #E5E5E0' }}>
                              <th className="text-left uppercase" style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, letterSpacing: '0.5px', color: '#666666' }}>Area / Post</th>
                              <th className="text-left uppercase" style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, letterSpacing: '0.5px', color: '#666666' }}>Staff Assigned</th>
                              <th className="text-left uppercase" style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, letterSpacing: '0.5px', color: '#666666', width: '40%' }}>Distribution</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(reportData.towerAllocation).sort((a, b) => b[1] - a[1]).map(([area, count]) => {
                              const maxCount = Math.max(...Object.values(reportData.towerAllocation));
                              return (
                                <tr key={area} style={{ borderBottom: '1px solid #F0F0EB' }}>
                                  <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 500, color: '#1A1A1A' }}>{area}</td>
                                  <td style={{ padding: '12px 16px', fontSize: '13px', color: '#1A1A1A' }}>{count}</td>
                                  <td style={{ padding: '12px 16px' }}>
                                    <div style={{ background: '#F0F0EB', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
                                      <div style={{ width: `${(count / maxCount) * 100}%`, height: '100%', background: '#043959', borderRadius: '4px' }} />
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>

                {/* Working Days Coverage */}
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#666666', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>Working Days Coverage</div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7" style={{ gap: '8px' }}>
                    {Object.entries(reportData.dayCoverage).map(([day, count]) => {
                      const maxDay = Math.max(...Object.values(reportData.dayCoverage), 1);
                      const intensity = count / maxDay;
                      return (
                        <div
                          key={day}
                          className="bg-white text-center"
                          style={{
                            padding: '14px 8px', borderRadius: '10px',
                            border: `1px solid ${intensity < 0.5 && count > 0 ? '#F59E0B' : '#E5E5E0'}`,
                          }}
                        >
                          <div className="text-lg font-bold" style={{ color: '#1A1A1A' }}>{count}</div>
                          <div style={{ fontSize: '11px', color: '#666666', marginTop: '2px' }}>{day.slice(0, 3)}</div>
                          <div style={{ marginTop: '6px', background: '#F0F0EB', borderRadius: '3px', height: '4px', overflow: 'hidden' }}>
                            <div style={{ width: `${intensity * 100}%`, height: '100%', background: intensity < 0.5 ? '#F59E0B' : '#10B981', borderRadius: '3px' }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Add Staff Modal */}
      <Modal isOpen={showAddStaffModal} onClose={() => { setShowAddStaffModal(false); setEditingStaffId(null); }} title={editingStaffId ? 'Edit Staff' : 'Add Staff'} size="lg">
        <form onSubmit={handleAddStaffSubmit} className="space-y-4">
          <FormInput label="Staff Name *" name="name" value={addStaffForm.name} onChange={handleAddStaffChange} placeholder="Enter staff name" required />
          <FormInput label="Phone" name="phone" value={addStaffForm.phone} onChange={handleAddStaffChange} placeholder="Enter phone number" />

          {/* Staff Type */}
          <div>
            <label className="block text-sm font-medium" style={{ color: '#1A1A1A', marginBottom: '6px' }}>Staff Type *</label>
            <select
              name="staff_type"
              value={addStaffForm.staff_type}
              onChange={(e) => setAddStaffForm({ ...addStaffForm, staff_type: e.target.value })}
              className="w-full cursor-pointer"
              style={{
                padding: '10px 12px', border: '1px solid #E5E5E0', borderRadius: '8px',
                fontSize: '14px', fontFamily: "'Lexend', sans-serif", background: 'white', outline: 'none',
              }}
            >
              <option value="">Select Type</option>
              <option value="cleaning">Cleaning Staff</option>
              <option value="security">Security Staff</option>
            </select>
          </div>

          {/* Working Hours */}
          <div>
            <label className="block text-sm font-medium" style={{ color: '#1A1A1A', marginBottom: '6px' }}>Working Hours *</label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label style={{ fontSize: '12px', color: '#999999', marginBottom: '4px', display: 'block' }}>Start Time</label>
                <input
                  type="time"
                  name="work_start_time"
                  value={addStaffForm.work_start_time}
                  onChange={handleAddStaffChange}
                  className="w-full"
                  style={{
                    padding: '10px 12px', border: '1px solid #E5E5E0', borderRadius: '8px',
                    fontSize: '14px', fontFamily: "'Lexend', sans-serif", outline: 'none',
                  }}
                  required
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#999999', marginBottom: '4px', display: 'block' }}>End Time</label>
                <input
                  type="time"
                  name="work_end_time"
                  value={addStaffForm.work_end_time}
                  onChange={handleAddStaffChange}
                  className="w-full"
                  style={{
                    padding: '10px 12px', border: '1px solid #E5E5E0', borderRadius: '8px',
                    fontSize: '14px', fontFamily: "'Lexend', sans-serif", outline: 'none',
                  }}
                  required
                />
              </div>
            </div>
          </div>

          {/* Working Days */}
          <div>
            <label className="block text-sm font-medium" style={{ color: '#1A1A1A', marginBottom: '6px' }}>Working Days *</label>
            <div className="flex flex-wrap gap-2" style={{ marginTop: '8px' }}>
              {allDays.map((day) => (
                <div
                  key={day}
                  onClick={() => toggleAddStaffDay(day)}
                  className="cursor-pointer inline-flex items-center justify-center"
                  style={{
                    minWidth: '36px', padding: '4px 8px', borderRadius: '6px',
                    fontSize: '11px', fontWeight: 600, userSelect: 'none',
                    background: addStaffForm.working_days.includes(day) ? '#043959' : 'white',
                    color: addStaffForm.working_days.includes(day) ? 'white' : '#999999',
                    border: addStaffForm.working_days.includes(day) ? '1.5px solid #043959' : '1.5px solid #E5E5E0',
                    transition: 'all 0.2s',
                  }}
                >
                  {dayLabels[day]}
                </div>
              ))}
            </div>
          </div>

          {/* Photo & Aadhar Upload */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium" style={{ color: '#1A1A1A', marginBottom: '6px' }}>Profile Photo</label>
              <label
                className="flex items-center gap-2 cursor-pointer"
                style={{
                  padding: '10px 12px', border: '1px dashed #E5E5E0', borderRadius: '8px',
                  background: '#FAFAF8', transition: 'all 0.2s',
                }}
              >
                {photoPreview ? (
                  <img src={photoPreview} alt="Preview" style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover' }} />
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#999999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                    <circle cx="12" cy="13" r="4"/>
                  </svg>
                )}
                <span style={{ fontSize: '13px', color: photoFile ? '#1A1A1A' : '#999999' }}>
                  {photoFile ? photoFile.name : 'Choose photo'}
                </span>
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setPhotoFile(file);
                      setPhotoPreview(URL.createObjectURL(file));
                    }
                  }}
                />
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium" style={{ color: '#1A1A1A', marginBottom: '6px' }}>Aadhar Document</label>
              <label
                className="flex items-center gap-2 cursor-pointer"
                style={{
                  padding: '10px 12px', border: '1px dashed #E5E5E0', borderRadius: '8px',
                  background: '#FAFAF8', transition: 'all 0.2s',
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#999999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                <span style={{ fontSize: '13px', color: aadharFile ? '#1A1A1A' : '#999999' }}>
                  {aadharFile ? aadharFile.name : 'Choose file (image/PDF)'}
                </span>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setAadharFile(file);
                    }
                  }}
                />
              </label>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4" style={{ borderTop: '1px solid #E5E5E0' }}>
            <button
              type="button"
              onClick={() => { setShowAddStaffModal(false); setEditingStaffId(null); }}
              className="text-sm font-medium rounded-lg transition-all cursor-pointer"
              style={{
                padding: '10px 20px', background: '#FAFAF8', color: '#1A1A1A',
                border: '1px solid #E5E5E0', fontFamily: "'Lexend', sans-serif",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={addStaffSubmitting}
              className="text-sm font-medium rounded-lg text-white transition-all cursor-pointer border-0"
              style={{
                padding: '10px 20px', background: '#043959',
                fontFamily: "'Lexend', sans-serif", opacity: addStaffSubmitting ? 0.7 : 1,
              }}
            >
              {addStaffSubmitting ? (editingStaffId ? 'Saving...' : 'Adding...') : (editingStaffId ? 'Save Changes' : 'Add Staff')}
            </button>
          </div>
        </form>
      </Modal>
    </AppLayout>
  );
}
